import React, {useEffect, useState} from 'react';
import styled from 'styled-components/native';
import {DefaultTheme, NavigationContainer} from '@react-navigation/native';
import {navigationRef} from '../navigation';
import {I18nManager, Platform} from 'react-native';
import {useTheme} from 'styled-components';
import {Theme} from '../theme/lightTheme';
import {useSelector} from 'react-redux';
import {RootState, store} from '../redux/store';
import Loader from '../components/loader';
import TextGeneric from '../components/textGeneric';
import {updateAppFont} from '../screens/content/fontChanger/actions';
import {getLocalData} from '../utils/storage';
import {keys} from '../api/keys';
import {login, setUniqueId} from '../screens/auth/login/reducer';
import {updateConfig} from '../screens/auth/login/actions';
import useDeepLinking from '../utils/hooks/useDeepLinking';
import useUniqueIdExtractor from '../utils/hooks/useUniqueIdExtractor';
import useEhrLinkHandler from '../utils/hooks/useEhrLinkHandler';
import {initOnboardingScreen} from '../screens/auth/onboarding/actions';
import screens from '../config/screens';
// Import the navigation config for path mapping
import navigationConfigWeb from '../navigation/navigationConfig';
import {initDarkMode} from '../screens/content/settings/action';
import {TTSService} from '../utils/services/TTSService/TTSService';
import BootSplash from 'react-native-bootsplash';
import {createNativeStackNavigator} from '@react-navigation/native-stack';
import linking from '../navigation/webNavigation';
import {isWeb} from '../utils/responsive';

// Import the route configurations directly
import contentRoutes from './mainRoutes/contentRoutes';
import authRoutes from './mainRoutes/authRoutes';
import onboardingRoutes from './mainRoutes/onboardingRoutes';
import sharedRoutes from './mainRoutes/sharedRoutes';

const Stack = createNativeStackNavigator();

const Container = styled.View`
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  background-color: ${({theme}) => theme.homeBackground};
`;
const Text = styled(TextGeneric)`
  padding-top: 4%;
  font-size: ${({theme}) => theme.text.s7}px;
  color: ${({theme}) => theme.text.dark};
`;

const SelectRouter = ({router}: any) => {
  // The custom hook for deep linking
  useDeepLinking();
  // The custom hook for UNID extraction
  useUniqueIdExtractor();
  // The custom hook for EHR id/token handling
  useEhrLinkHandler();

  const getAppropriateInitialRoute = (routerState: string): string => {
    switch (routerState) {
      case 'onboarding':
        return screens.onboarding;
      case 'auth':
        return screens.auth.login;
      case 'content':
        return screens.homeDrawer;
      default:
        return '';
    }
  };

  if (router === '') {
    return (
      <Container>
        <Loader />
        <Text>{I18nManager.isRTL ? 'جار التحميل...' : 'Loading...'}</Text>
      </Container>
    );
  }

  return (
    <Stack.Navigator
      key={`router-${router}`} // Add a key to force re-mount when router changes
      initialRouteName={getAppropriateInitialRoute(router)}
      screenOptions={{headerShown: false}}>
      {/* Only include routes relevant to current state */}
      {sharedRoutes.map(route => (
        <Stack.Screen
          key={route.name}
          name={route.name}
          component={route.component}
          options={route.options}
        />
      ))}
      {router === 'onboarding' &&
        onboardingRoutes.map(route => (
          <Stack.Screen
            key={route.name}
            name={route.name}
            component={route.component}
            options={route.options}
          />
        ))}
      {router === 'auth' &&
        authRoutes.map(route => (
          <Stack.Screen
            key={route.name}
            name={route.name}
            component={route.component}
            options={route.options}
          />
        ))}
      {router === 'content' &&
        contentRoutes.map(route => (
          <Stack.Screen
            key={route.name}
            name={route.name}
            component={route.component}
            options={route.options}
          />
        ))}
    </Stack.Navigator>
  );
};

/**
 * The main navigation component that handles routing and navigation between screens.
 * @constructor
 */
const AppNavigator = () => {
  /**
   * The current theme used in the navigation component.
   * @type {Theme}
   */
  const theme: Theme = useTheme() as Theme;
  /**
   * The current router state, which determines whether to show 'content' or 'auth' routes.
   * @type {'content' | 'auth' | ''}
   */
  const [router, setRouter] = useState<'auth' | 'content' | 'onboarding' | ''>(
    '',
  );
  const [isNavigationReady, setIsNavigationReady] = useState(false);
  const userStatus = useSelector((state: RootState) => state.auth);
  const {isSeen: isOnboardingSeen} = useSelector(
    (state: RootState) => state.onboarding,
  );


  /**
   * Initialize routes and check user login status on component mount.
   */
  useEffect(() => {
    initRouter();
    updateAppFont(undefined, true);
    // Start the TTS service
    TTSService.getInstance().init({
      language: 'en-US',
      rate: 0.5,
      pitch: 1.0,
      ducking: true,
      iosVoiceId: 'com.apple.ttsbundle.Daniel-compact',
    });
  }, []);

  const MyTheme = {
    ...DefaultTheme,
    colors: {
      ...DefaultTheme.colors,
      primary: theme.primary,
    },
  };

  /**
   * Initialize routes by checking the user's login status in local storage.
   */
  const initRouter = async () => {
    await initOnboardingScreen();

          if (isWeb) {
        // UNID-based initialization for web
        const rawUsers = getLocalData(keys.CURRENT_USER);
        const users = rawUsers && rawUsers.default ? rawUsers : rawUsers || {};
        const searchParams = new URLSearchParams(window.location.search);
        const unid = searchParams.get('unid');

        if (users && unid && users[unid]) {
          const selected = users[unid];
          store.dispatch(setUniqueId(unid));
          if (selected && selected.token) {
            store.dispatch(login(selected));
          } else {
            store.dispatch(login({token: null}));
            setRouter('auth');
          }
        } else if (users && users.default && !unid) {
          const selected = users.default;
          if (selected && selected.token) {
            store.dispatch(login(selected));
            store.dispatch(setUniqueId(null));
          } else {
            store.dispatch(login({token: null}));
            store.dispatch(setUniqueId(null));
            setRouter('auth');
          }
        } else {
          store.dispatch(login({token: null}));
          store.dispatch(setUniqueId(unid));
          setRouter('auth');
        }
      } else {
      // Traditional initialization for mobile
      const currentUser = getLocalData(keys.CURRENT_USER);
      if (currentUser && Object.keys(currentUser).length !== 0) {
        store.dispatch(login(currentUser));
      } else {
        store.dispatch(login({token: null}));
      }
    }
    // initDarkMode();
  };

  /**
   * Check user login status and set the correct routes ('content' or 'auth').
   */
  const checkLogin = () => {
    if (isOnboardingSeen === false) {
      setRouter('onboarding');
      return true;
    } else if (userStatus && userStatus?.currentUser?.token) {
      setRouter('content');
      return true;
    } else if (userStatus?.currentUser?.token === null) {
      setRouter('auth');
      return true;
    }

    return false;
  };


  useEffect(() => {
    let handled = false;

    handled = checkLogin();
    // fallback after 5 seconds if not handled
    const timeout = setTimeout(() => {
      if (!handled) {
        setRouter('auth');
      }
    }, 5000);
    return () => {
      clearTimeout(timeout);
    };
  }, [userStatus, isOnboardingSeen]);

  useEffect(() => {
    if (userStatus && userStatus?.currentUser?.token) {
      // Lazy load and execute getHomeTabs
      import('../screens/content/home/actions').then(module => {
        module.getHomeTabs();
      });
    }
  }, [userStatus?.currentUser]);

  useEffect(() => {
    if (userStatus?.currentUser?.token) {
      updateConfig();
    }
  }, [userStatus?.currentUser?.token]);

  // Reset navigation when router state changes
  useEffect(() => {
    if (router === '') return;

    if (Platform.OS !== 'web') {
      // For mobile, always reset to the initial screen
      const initialScreen =
        router === 'onboarding'
          ? screens.onboarding
          : router === 'auth'
            ? screens.auth.login
            : screens.homeDrawer;

      // Use timeout to ensure state is settled before navigation
      setTimeout(() => {
        navigationRef.current?.reset({
          index: 0,
          routes: [{name: initialScreen}],
        });
      }, 100);
    }
  }, [router]);

  const isScreenInRoutes = (screenName: string, routes: any[]): boolean => {
    return routes.some(route => route.name === screenName);
  };

  // helper to get the deepest active route name from navigation state
  function getActiveRouteName(state: any): string {
    const route = state.routes[state.index];
    // dive into nested navigators if needed
    if (route.state) {
      return getActiveRouteName(route.state);
    }
    return route.name;
  }

  useEffect(() => {
    if (isWeb) {
      BootSplash.hide({fade: true});
    }
    handleHideSplash();
  }, [router, isNavigationReady]);

  const handleHideSplash = async () => {
    if (router !== '' && isNavigationReady) {
      await initDarkMode();
      await BootSplash.hide({fade: true});
    }
  };

  const handleNavigationReady = () => {
    setIsNavigationReady(true);
  };

  const handleStateChange = (state: any) => {
    if (Platform.OS !== 'web' || !state) return;

    const activeRouteName = getActiveRouteName(state);
    // On page refresh, don't force navigation changes
    if (window.performance && window.performance.navigation.type === 1) {
      return; // If it's a page refresh, don't redirect
    }

    const isOnboardingRoute = isScreenInRoutes(
      activeRouteName,
      onboardingRoutes,
    );
    const isAuthRoute = isScreenInRoutes(activeRouteName, authRoutes);
    const isContentRoute = isScreenInRoutes(activeRouteName, contentRoutes);

    const isAuthenticated = router === 'content';
    const isFirstTime = router === 'onboarding';
    const isNotAuthenticated = router === 'auth';

    // If authenticated and on auth or onboarding screen, go to home
    if (isAuthenticated && (isAuthRoute || isOnboardingRoute)) {
      if (activeRouteName !== screens.homeDrawer) {
        navigationRef.current?.reset({
          index: 0,
          routes: [{name: screens.homeDrawer}], // Use homeDrawer for content routes
        });
      }
      return;
    }

    // If not authenticated and on content screen, go to login
    if (isNotAuthenticated && isContentRoute) {
      if (activeRouteName !== screens.auth.login) {
        navigationRef.current?.reset({
          index: 0,
          routes: [{name: screens.auth.login}],
        });
      }
      return;
    }

    // If onboarding is not complete and not on onboarding screen
    if (isFirstTime && !isOnboardingRoute) {
      if (activeRouteName !== screens.onboarding) {
        navigationRef.current?.reset({
          index: 0,
          routes: [{name: screens.onboarding}],
        });
      }
      return;
    }
  };

  return (
    <NavigationContainer
      onReady={handleNavigationReady}
      onStateChange={handleStateChange}
      linking={
        Platform.OS === 'web'
          ? {
              prefixes: linking.prefixes,
              config: {
                screens: navigationConfigWeb.screens,
                path: navigationConfigWeb.config.path,
                initialRouteName: navigationConfigWeb.config.initialRouteName,
              },
              getPathFromState: linking.getPathFromState,
            }
          : undefined
      }
      theme={MyTheme}
      ref={navigationRef}>
      <SelectRouter router={router} />
    </NavigationContainer>
  );
};

export default AppNavigator;
